import { merge } from 'lodash-es';
import {
  CryptoService,
  CryptoUtilities,
  CryptoWorkerService,
  encryptionServiceInstance,
} from 'securedrive-crypto';
import { FileInfo, VirtualFileSystemProp } from '../../types';
import {
  getEncryptedKeychain,
  getEncryptedMasterKey,
} from '../encryption/encryption.service';
import { SessionService } from '../session/session.service';
import { OperationSpec } from '../transactions/models/OperationContext';

export type OperationMethod =
  | 'PUT'
  | 'COPY'
  | 'DELETE'
  | 'RESTORE'
  | 'DELETE_VERSION';
export interface RequestOptions {
  requestId?: string;
  operation: OperationMethod;
  pathname: string;
  paired_pathname?: string | null;
  content_md5?: string;
  content_type?: string | null;
  content_length?: number;
  encryption_iv?: string | null;
  encryption_dek?: string | null;
  version_id?: number | null;
  inbox_content_id?: string | null;
  content?: any | null;
  from?: EncryptedFile | null;
}

export interface EncryptedFile extends Blob {
  iv: string;
  md5: string;
  name: string;
}

export const defaultRequestOptions: Partial<RequestOptions> = {
  content: null,
  pathname: '',
  paired_pathname: null,
  content_md5: '',
  content_type: '',
  content_length: 0,
  encryption_iv: null,
  encryption_dek: null,
  inbox_content_id: null,
  version_id: null,
};

export const prepareRequestOptions = async (
  options: OperationSpec,
  extraRequestOptions: Partial<RequestOptions> = {}
): Promise<RequestOptions> => {
  const { file, path, from, to, isEncryptedZone = true, type } = options;
  //debugger;
  const operation = getOperationAction(options);
  const content_length = getContentLength(options);
  const content_md5 = await getContentMd5(options);
  const content_type =
    operation === 'DELETE' || operation === 'COPY'
      ? null
      : getContentType(options);

  //se è un'operazione di delete o copy, il pathname è il path di destinazione, altrimenti è il path di origine
  let pathname =
    (operation === 'DELETE' || operation === 'COPY' ? to || from : from) || '';

  //se è un'operazione di delete o copy, il paired_pathname è il path di origine, altrimenti è null
  const paired_pathname = (operation === 'COPY' ? from : null) || null;

  //controllo se è una folder
  if (options.type.includes('folder')) {
    //se è una folder.update, il pathname è il to
    if (type === 'folder.update' && to) {
      pathname = to;
    }
    //il pathname deve essere un path con / al termine
    if (!pathname.endsWith('/')) {
      pathname = pathname + '/';
    }
  }

  //Se isEncryptedZone è true, allora encryption_iv e encryption_dek devono essere valorizzati
  let encryption_iv = null;
  let encryption_dek = null;

  let requestOptions: RequestOptions = {
    operation,
    pathname,
    content_md5,
    content_type,
    content_length,
    encryption_iv,
    encryption_dek,
  };

  if (operation === 'DELETE' || operation === 'COPY') {
    requestOptions.paired_pathname = paired_pathname;
  }

  const merged = merge(
    {},
    defaultRequestOptions,
    requestOptions,
    extraRequestOptions
  );

  return merged as RequestOptions;
};

export const getOperationAction = (options: OperationSpec): OperationMethod => {
  switch (options.type) {
    case 'file.upload':
      return 'PUT';
    case 'file.delete':
    case 'folder.delete':
      return 'DELETE';

    case 'file.update':
    case 'file.move':
      return 'COPY';
    default:
      return 'PUT';
  }
};

export const getContentLength = (options: OperationSpec) => {
  return options.type === 'file.upload' ? options.file?.size : 0;
};

export const getContentMd5 = async (options: OperationSpec) => {
  if (options.type.includes('folder')) {
    return 'd41d8cd98f00b204e9800998ecf8427e';
  }
  if (options.type === 'file.upload') {
    return CryptoUtilities.computeMD5(options.file as File);
  }

  return (options.file as FileInfo)?.etag;
};

export const getContentType = (options: OperationSpec) => {
  // se è una folder, il content_type è SEMPRE application/octet-stream
  if (options.type.includes('folder')) {
    return 'application/octet-stream';
  }
  if (!options.file) {
    return null;
  }
  // Implementazione semplificata, da espandere con una mappatura più completa
  const file = options.file as File;
  const ext = file?.name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'txt':
      return 'text/plain';
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    default:
      return 'application/octet-stream';
  }
};

export const getFilePath = (
  file: FileInfo | File,
  path: VirtualFileSystemProp,
  subPath: string[] | null
) => {
  if (typeof file === 'object' && 'key' in file) {
    return file.key;
  } else {
    let extraPath = '';
    if (subPath && subPath.length > 0) {
      extraPath = subPath.join('/') + '/';
    }
    return `${path}/${extraPath}${file.name}`;
  }
};

export const getFolderPathFromPath = (
  path: VirtualFileSystemProp,
  subPath: string[] | null,
  folderName: string
) => {
  let extraPath = '';
  if (subPath && subPath.length > 0) {
    extraPath = subPath.join('/') + '/';
  }
  return `${path}/${extraPath}${folderName}/`;
};

export const getEncryptionKeys = async (
  options: OperationSpec
): Promise<{
  encryptedFile: EncryptedFile;
  requestOptions: Partial<RequestOptions>;
}> => {
  const b64_ez_key = await getB64_EZ_Key();

  //CRIPTA IL File
  const cryptoService = CryptoService.getInstance();
  const workerService = CryptoWorkerService.getInstance();

  const iv = await cryptoService.generateIV();
  const dek = await cryptoService.generateDek();

  const fileData = await (options.file as File).arrayBuffer();

  try {
    const encrypted_file = await workerService.encryptFile(dek, fileData, iv);

    let encryptBlob: Blob = new Blob([new Uint8Array(encrypted_file.data)], {
      type: 'application/octet-stream',
    });

    //const md5 = await CryptoUtilities.computeMD5(encrypted_file.data)

    //TODO calcola il content_md5
    const content_md5 = encrypted_file.hash;
    const content_length = encryptBlob.size;
    const encryption_iv = CryptoUtilities.arrayBufferToBase64(iv);
    const ez_key = CryptoUtilities.base64ToArrayBuffer(b64_ez_key);

    const dek_iv = await cryptoService.generateIV();
    const enc_dek_array_buffer = await CryptoService.getInstance().encryptCBC(
      ez_key,
      dek,
      dek_iv
    );

    const encryption_dek =
      CryptoUtilities.arrayBufferToBase64(enc_dek_array_buffer);

    let encryptedFile: Partial<EncryptedFile> = encryptBlob;
    encryptedFile.iv = encryption_iv;
    encryptedFile.md5 = content_md5;
    encryptedFile.name = (options.file as File).name;

    return {
      encryptedFile: encryptedFile as EncryptedFile,
      requestOptions: {
        content_md5,
        //content_type: getContentType(options),
        content_length,
        encryption_iv,
        encryption_dek,
      },
    };
  } catch (error) {
    console.error('Encryption failed:', error);
    throw error;
  }
};

export const getB64_EZ_Key = async (): Promise<string> => {
  //TODO prendi password utente se non è già in memoria
  //modale con passsowrd

  const cypherCode = await SessionService.getCypherCode();
  if (!cypherCode) {
    throw new Error('Cypher key not found');
  }
  const { enc_mk } = await getEncryptedMasterKey();

  const username = (
    await SessionService.getSession()
  ).provisioning_id.toLowerCase();

  //decripted master key base64
  const masterKey = await encryptionServiceInstance.decryptMasterKey(
    username,
    cypherCode,
    enc_mk
  );

  //encrypted keychain base64
  const b64Data = await getEncryptedKeychain();

  const { EZ_personal: b64_ez_key } =
    await encryptionServiceInstance.decryptKeyChain(masterKey, b64Data);

  return b64_ez_key;
};
